// Parts Finder Vehicle Selector — renders Make / Model / Variant / Year step
// fields matching the React app VehicleSelector, then navigates to /parts-finder.
//
// Config (read from the TARGET element, not the script tag):
//   [data-parts-vehicle-selector]  — the container the widget renders into
//
// Single-instance widget: module-level state is shared across the one container.

import { getTopDocument, esc } from '../shared/dom.js';
import { getAppBaseUrl } from '../shared/config.js';
import { apiFetch } from '../shared/api.js';
import { ELLIPSIS_CSS, decorateTruncated, markTriggerTitles, bindTruncTips, showTipForRow, hideTip } from '../shared/trunc-tip.js';

var CONTAINER_SELECTOR = '[data-parts-vehicle-selector]';

var _container = null;
var _data = { makes: [], models: {}, years: [], yearsByModel: {}, engines: {} };
var _sel = { make: '', model: '', engine: '', year: '' };
var _openDropdown = null; // id of currently open dropdown
var _highlight = 0; // index of the keyboard-highlighted option in the open dropdown

// Makes that render Year as step 2 instead of Model. Must stay in sync with
// app/src/utils/vehicleSelectorRules.ts — the React sidebar/landing picker
// uses the same list. Storefront embeds are shipped as plain JS so the
// utility can't be imported directly.
var YEAR_FIRST_MAKES = { FORD: 1, TOYOTA: 1 };

function isYearFirstMake(make) {
	return !!(make && YEAR_FIRST_MAKES[String(make).trim().toUpperCase()]);
}

/** Field order for the currently-selected make. Ford / Toyota get Year in
 *  slot 2 (backwards-filter narrows the huge model list); everyone else
 *  gets Year last. */
function orderedFields() {
	return isYearFirstMake(_sel.make)
		? ['make', 'year', 'model', 'engine']
		: ['make', 'model', 'engine', 'year'];
}
function nextField(fid) {
	var order = orderedFields();
	var idx = order.indexOf(fid);
	return (idx < 0 || idx === order.length - 1) ? null : order[idx + 1];
}
function prevField(fid) {
	var order = orderedFields();
	var idx = order.indexOf(fid);
	return (idx <= 0) ? null : order[idx - 1];
}

// ─── SVG icons ────────────────────────────────────────────────────────────
var ICON_CHEVRON = '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/></svg>';
var ICON_X      = '<svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>';
var ICON_CHECK  = '<svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>';
var ICON_ARROW  = '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/><path stroke-linecap="round" stroke-linejoin="round" d="m12 5 7 7-7 7"/></svg>';
var ICON_CHECKBIG = '<svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>';

// ─── Progress helpers ─────────────────────────────────────────────────────
function completedSteps() {
	return [_sel.make, _sel.model, _sel.engine, _sel.year].filter(Boolean).length;
}
function progressMessage() {
	var n = completedSteps();
	if (n === 0) return 'Start by selecting a make';
	if (n === 1) return 'Now choose your model';
	if (n === 2) return 'Now choose a variant';
	if (n === 3) return 'Now choose the year';
	return 'Vehicle selected. Browse parts below';
}
function hasAny() { return !!((_sel.make) || (_sel.model) || (_sel.engine) || (_sel.year)); }
function allDone() { return completedSteps() === 4; }

// ─── Close any open dropdown ──────────────────────────────────────────────
function closeAll(except) {
	['make','model','engine','year'].forEach(function(id) {
		if (id === except) return;
		var combo = _container.querySelector('.pvs-combo[data-pvs-combo="'+id+'"]');
		if (combo) combo.classList.remove('open');
	});
	_openDropdown = except || null;
	hideTip();
}

// ─── Programmatically open a specific field's dropdown ────────────────────
// Same behaviour as clicking the field's trigger button: closes any
// currently-open combo, opens the target combo, clears + focuses its
// search input. Used by advance()/retreat() to power Tab-based navigation.
function openField(fid) {
	if (!fid) return false;
	var combo = _container.querySelector('.pvs-combo[data-pvs-combo="'+fid+'"]');
	if (!combo) return false;
	var btn = combo.querySelector('[data-pvs-btn]');
	// Don't open a disabled step (e.g. Variant before a model is picked) —
	// let the caller fall through to whatever comes next.
	if (btn && btn.disabled) return false;
	closeAll(null);
	combo.classList.add('open');
	_openDropdown = fid;
	if (btn) btn.setAttribute('aria-expanded', 'true');
	var si = combo.querySelector('[data-pvs-search]');
	if (si) {
		si.value = '';
		refreshOptions(fid);
		setTimeout(function(){ si.focus(); }, 0);
	}
	return true;
}

function focusFindParts() {
	var findBtn = _container.querySelector('[data-pvs-findparts]');
	if (findBtn && !findBtn.disabled) {
		setTimeout(function(){ findBtn.focus(); }, 0);
		return true;
	}
	return false;
}

/** Move focus to the next step in the current (year-first-aware) order.
 *  If already on the last step, focus the primary "Find Parts" action.
 *  Mirrors VehicleSelector.tsx's onAdvance handlers. */
function advance(fromFid) {
	var nxt = nextField(fromFid);
	if (nxt) return openField(nxt);
	return focusFindParts();
}
/** Move focus back to the previous step. Mirrors onRetreat in React. */
function retreat(fromFid) {
	var prv = prevField(fromFid);
	if (prv) return openField(prv);
	return false;
}

// ─── Build one step field ─────────────────────────────────────────────────
function buildStep(id, stepNum, label, placeholder, options, disabled) {
	var val = _sel[id];
	var isCompleted = !!val;
	var isActive = !disabled && !val;

	var wrap = document.createElement('div');
	wrap.className = 'pvs-step' + (disabled ? ' pvs-disabled' : '');
	wrap.setAttribute('data-pvs-step', id);

	// Step header (number circle + label)
	var header = document.createElement('div');
	header.className = 'pvs-step-header';

	var numCircle = document.createElement('span');
	numCircle.className = 'pvs-step-num ' + (isCompleted ? 'done' : isActive ? 'active' : 'inactive');
	numCircle.innerHTML = isCompleted ? ICON_CHECKBIG : String(stepNum);

	var lbl = document.createElement('span');
	lbl.className = 'pvs-step-label';
	lbl.textContent = label;

	header.appendChild(numCircle);
	header.appendChild(lbl);
	wrap.appendChild(header);

	// Combobox wrapper
	var combo = document.createElement('div');
	combo.className = 'pvs-combo';
	combo.setAttribute('data-pvs-combo', id);

	// Dropdown button (visible when closed)
	var btnClasses = 'pvs-btn' + (val ? ' selected' : '') + (disabled ? ' disabled-look' : '');
	var btn = document.createElement('button');
	btn.type = 'button';
	btn.className = btnClasses;
	btn.setAttribute('data-pvs-btn', id);
	btn.setAttribute('aria-haspopup', 'listbox');
	btn.setAttribute('aria-expanded', 'false');
	// Disabled steps (e.g. Variant before a model is chosen) are taken out
	// of the tab order and can't be opened by keyboard, so Tab skips
	// straight to the next usable field.
	btn.disabled = !!disabled;

	var btnText = document.createElement('span');
	btnText.className = 'pvs-btn-text';
	btnText.style.cssText = ELLIPSIS_CSS;
	btnText.textContent = val || placeholder;
	btn.appendChild(btnText);

	var iconSpan = document.createElement('span');
	if (val) {
		iconSpan.className = 'pvs-btn-icon pvs-clear';
		iconSpan.innerHTML = ICON_X;
		iconSpan.setAttribute('data-pvs-clear', id);
		iconSpan.title = 'Clear';
	} else {
		iconSpan.className = 'pvs-btn-icon';
		iconSpan.innerHTML = ICON_CHEVRON;
	}
	btn.appendChild(iconSpan);
	combo.appendChild(btn);

	// Inline search input (visible when open — replaces button)
	var searchInput = document.createElement('input');
	searchInput.type = 'text';
	searchInput.className = 'pvs-search-inline';
	searchInput.placeholder = 'Type to search ' + label.toLowerCase() + '...';
	searchInput.setAttribute('data-pvs-search', id);
	combo.appendChild(searchInput);

	// Dropdown panel (options list only, no search box inside)
	var panel = document.createElement('div');
	panel.className = 'pvs-panel';
	panel.setAttribute('data-pvs', id);

	var optList = document.createElement('div');
	optList.className = 'pvs-options';
	optList.setAttribute('role', 'listbox');
	optList.setAttribute('data-pvs-list', id);
	renderOptions(optList, options, val);
	panel.appendChild(optList);
	combo.appendChild(panel);

	wrap.appendChild(combo);
	return wrap;
}

function renderOptions(container, options, selected) {
	container.innerHTML = '';
	options.forEach(function(opt) {
		var optEl = document.createElement('div');
		optEl.className = 'pvs-option' + (String(opt) === String(selected) ? ' selected-opt' : '');
		optEl.setAttribute('role', 'option');
		// One line per option, ellipsised at the field's width; truncated rows
		// get an info icon + full-name tooltip once laid out (decorateTruncated).
		optEl.style.display = 'flex'; optEl.style.alignItems = 'center';
		optEl.setAttribute('data-pvs-value', String(opt));
		optEl.setAttribute('aria-selected', String(opt) === String(selected) ? 'true' : 'false');

		var textSpan = document.createElement('span');
		textSpan.className = 'pvs-opt-text';
		textSpan.style.cssText = ELLIPSIS_CSS;
		textSpan.textContent = String(opt);
		optEl.appendChild(textSpan);

		if (String(opt) === String(selected)) {
			var chk = document.createElement('span');
			chk.className = 'pvs-opt-check';
			chk.innerHTML = ICON_CHECK;
			optEl.appendChild(chk);
		}
		container.appendChild(optEl);
	});
}

// ─── Backwards-filter: models narrowed by selected year ──────────────────
// When the user picks year before model (or picks year first), narrow the
// model list to only those active in that year. Mirrors the same logic in
// VehicleSelector.tsx (availableModels useMemo). yearsByModel is the
// expanded year set per (make, model) — see api/src/routes/filters.ts.
function modelsForMake() {
	if (!_sel.make) return [];
	var all = _data.models[_sel.make] || [];
	if (!_sel.year) return all;
	var ym = _data.yearsByModel && _data.yearsByModel[_sel.make];
	if (!ym) return all;
	var y = Number(_sel.year);
	return all.filter(function (m) {
		var ys = ym[m];
		return ys && ys.indexOf(y) !== -1;
	});
}

// ─── Render full widget ───────────────────────────────────────────────────
function render() {
	var done = completedSteps();
	var pct = (done / 4) * 100;
	var engines = (_sel.make && _sel.model && _data.engines[_sel.make] && _data.engines[_sel.make][_sel.model])
		? _data.engines[_sel.make][_sel.model] : [];
	var models = modelsForMake();

	var frag = document.createDocumentFragment();

	// Progress
	var progWrap = document.createElement('div');
	progWrap.className = 'pvs-progress-wrap';
	progWrap.innerHTML = '<div class="pvs-progress-row"><span class="pvs-progress-msg">'+esc(progressMessage())+'</span><span>'+done+'/4</span></div>'
		+ '<div class="pvs-progress-track"><div class="pvs-progress-fill" style="width:'+pct+'%"></div></div>';
	frag.appendChild(progWrap);

	// Card
	var card = document.createElement('div');
	card.className = 'pvs-card';

	var hint = document.createElement('p');
	hint.className = 'pvs-card-hint';
	hint.textContent = 'Select a new vehicle below:';
	card.appendChild(hint);

	// Steps row
	var stepsRow = document.createElement('div');
	stepsRow.className = 'pvs-steps';

	// Per-make journey override — Ford / Toyota render Year as step 2
	// instead of Model (the catalogues are year-driven and the model
	// list is huge, so picking the year first narrows it via the
	// existing yearsByModel backwards-filter). Mirrors
	// app/src/utils/vehicleSelectorRules.ts via the shared
	// YEAR_FIRST_MAKES constant at the top of this file.
	var yearOptions = getOptions('year');
	var yearFirst = isYearFirstMake(_sel.make);

	// Each step definition is built once, then rendered in the chosen
	// order with arrows between. Step numbers are positional so the
	// UI always reads "1 → 2 → 3 → 4" regardless of which field came
	// second.
	var stepDefs = {
		make:   { id: 'make',   label: 'MAKE',    placeholder: 'Select Make...',                                                          options: _data.makes,  disabled: false },
		model:  { id: 'model',  label: 'MODEL',   placeholder: _sel.make ? 'Select Model...' : 'Select make first',                       options: models,       disabled: !_sel.make },
		engine: { id: 'engine', label: 'VARIANT', placeholder: _sel.model ? 'Select Variant...' : 'Select model first',                    options: engines,      disabled: !_sel.model },
		year:   { id: 'year',   label: 'YEAR',    placeholder: _sel.model ? 'Select Year...' : 'Select model first',                       options: yearOptions,  disabled: false },
	};
	// Same order as orderedFields() — kept inline so step numbers/arrows
	// stay in sync with the picker layout.
	var order = orderedFields();
	for (var si = 0; si < order.length; si++) {
		var def = stepDefs[order[si]];
		stepsRow.appendChild(buildStep(def.id, si + 1, def.label, def.placeholder, def.options, def.disabled));
		if (si < order.length - 1) stepsRow.appendChild(buildArrow());
	}

	card.appendChild(stepsRow);

	// Footer
	var footer = document.createElement('div');
	footer.className = 'pvs-footer';

	var footerLeft = document.createElement('div');
	if (hasAny()) {
		var clearBtn = document.createElement('button');
		clearBtn.type = 'button';
		clearBtn.className = 'pvs-clear-all';
		clearBtn.textContent = 'Clear all selections';
		clearBtn.setAttribute('data-pvs-clearall', 'true');
		footerLeft.appendChild(clearBtn);
	}
	footer.appendChild(footerLeft);

	var footerRight = document.createElement('div');
	footerRight.className = 'pvs-footer-right';

	var hintMsg = document.createElement('span');
	hintMsg.className = 'pvs-hint-msg';
	hintMsg.textContent = allDone() ? 'All selections complete' : progressMessage();
	footerRight.appendChild(hintMsg);

	var findBtn = document.createElement('button');
	findBtn.type = 'button';
	// Make alone is enough to search; model/variant/year just narrow it.
	var canFind = !!_sel.make;
	findBtn.className = 'pvs-find-btn ' + (canFind ? 'ready' : 'not-ready');
	findBtn.disabled = !canFind;
	findBtn.setAttribute('data-pvs-findparts', 'true');
	findBtn.innerHTML = 'Find Parts ' + ICON_ARROW;
	footerRight.appendChild(findBtn);
	footer.appendChild(footerRight);
	card.appendChild(footer);

	frag.appendChild(card);

	_container.innerHTML = '';
	var wrap = document.createElement('div');
	wrap.className = 'pvs-wrap';
	wrap.appendChild(frag);
	_container.appendChild(wrap);

	// Attach events
	attachEvents();
	// Full value as a title on any field whose selection is cut off.
	markTriggerTitles(_container, _sel);
}

function buildArrow() {
	var div = document.createElement('div');
	div.className = 'pvs-arrow';
	div.innerHTML = ICON_ARROW;
	return div;
}

// ─── Event delegation ─────────────────────────────────────────────────────
function attachEvents() {
	var wrap = _container.querySelector('.pvs-wrap');
	if (!wrap) return;
	bindTruncTips(wrap);

	// Click handler (delegation)
	wrap.addEventListener('click', function(e) {
		// Find Parts button — fires as soon as a make is selected; the
		// rest of the vehicle fields are optional narrowing.
		if (e.target.closest('[data-pvs-findparts]') && _sel.make) {
			doFindParts();
			return;
		}
		// Clear all
		if (e.target.closest('[data-pvs-clearall]')) {
			_sel = { make: '', model: '', engine: '', year: '' };
			render();
			return;
		}
		// Clear individual field
		var clearEl = e.target.closest('[data-pvs-clear]');
		if (clearEl) {
			e.stopPropagation();
			var field = clearEl.getAttribute('data-pvs-clear');
			clearField(field);
			return;
		}
		// Dropdown option click
		var optEl = e.target.closest('.pvs-option');
		if (optEl) {
			var list = optEl.closest('[data-pvs-list]');
			var fid = list && list.getAttribute('data-pvs-list');
			if (fid) {
				selectValue(fid, optEl.getAttribute('data-pvs-value'));
				closeAll(null);
				// After selection, auto-advance focus to the next step
				// (or the Find Parts button if this was the last one).
				// Order respects year-first for Ford / Toyota.
				advance(fid);
			}
			return;
		}
		// Dropdown button toggle
		var dropBtn = e.target.closest('[data-pvs-btn]');
		if (dropBtn) {
			var bid = dropBtn.getAttribute('data-pvs-btn');
			var combo = _container.querySelector('.pvs-combo[data-pvs-combo="'+bid+'"]');
			if (!combo) return;
			var isOpen = combo.classList.contains('open');
			closeAll(null);
			if (!isOpen) {
				combo.classList.add('open');
				_openDropdown = bid;
				dropBtn.setAttribute('aria-expanded', 'true');
				var si = combo.querySelector('[data-pvs-search]');
				if (si) { si.value = ''; refreshOptions(bid); setTimeout(function(){si.focus();},0); }
			}
			return;
		}
		// Click outside any combo — close all
		if (!e.target.closest('.pvs-combo')) {
			closeAll(null);
		}
	});

	// Search inputs
	wrap.addEventListener('input', function(e) {
		var si = e.target.closest('[data-pvs-search]');
		if (!si) return;
		var fid = si.getAttribute('data-pvs-search');
		refreshOptions(fid, si.value);
	});

	// Prevent inline search input click from closing the dropdown
	wrap.addEventListener('mousedown', function(e) {
		if (e.target.closest('.pvs-search-inline') || e.target.closest('.pvs-panel')) e.stopPropagation();
	});

	// Keyboard navigation inside a search input:
	//   ArrowDown/Up → move the highlight through the visible options
	//   Enter        → select the highlighted option + auto-advance
	//   Tab          → same as Enter (commit highlight, advance) when the
	//                  list has a match; otherwise let native Tab move focus.
	//   Shift+Tab    → close current and open the previous step's dropdown
	//                  (respects year-first order for Ford / Toyota)
	//   Escape       → just close
	// Mirrors VehicleSelector.tsx's arrow/Enter/Tab handling.
	wrap.addEventListener('keydown', function(e) {
		// Closed trigger button: open on ArrowDown, or open seeded with the
		// first character the user types (Enter/Space open via the native
		// button click). Mirrors SearchableSelect.tsx's closed-state keys.
		var trigger = e.target.closest('[data-pvs-btn]');
		if (trigger) {
			var tid = trigger.getAttribute('data-pvs-btn');
			var tcombo = _container.querySelector('.pvs-combo[data-pvs-combo="'+tid+'"]');
			if (tcombo && !tcombo.classList.contains('open')) {
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					openField(tid);
				} else if ((e.key === 'Backspace' || e.key === 'Delete') && _sel[tid]) {
					// Keyboard equivalent of the mouse-only clear "x" (it is not a
					// Tab stop, so Tab always goes straight to the next field).
					e.preventDefault();
					clearField(tid);
					var again = _container.querySelector('[data-pvs-btn="'+tid+'"]');
					if (again) again.focus();
				} else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
					e.preventDefault();
					openField(tid);
					var seedInput = tcombo.querySelector('[data-pvs-search]');
					if (seedInput) { seedInput.value = e.key; refreshOptions(tid, e.key); }
				}
			}
			return;
		}
		var si = e.target.closest('.pvs-search-inline');
		if (!si) return;
		var fid = si.getAttribute('data-pvs-search');
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			moveHighlight(fid, 1);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			moveHighlight(fid, -1);
		} else if (e.key === 'Enter') {
			e.preventDefault();
			var opt = highlightedOption(fid);
			if (opt) {
				selectValue(fid, opt.getAttribute('data-pvs-value'));
				closeAll(null);
				advance(fid);
			}
		} else if (e.key === 'Tab' && e.shiftKey) {
			// Retreat only fires when there IS a previous field — on the
			// first field (Make) let native Shift+Tab move focus outside
			// the widget as usual.
			var prv = prevField(fid);
			if (prv) {
				e.preventDefault();
				closeAll(null);
				retreat(fid);
			}
		} else if (e.key === 'Tab') {
			// Forward Tab from search input: if the current query matches at
			// least one option, treat as "commit highlight + advance" so the
			// flow feels continuous. Otherwise, allow native Tab so the user
			// isn't trapped inside a dropdown with no match.
			var optTab = highlightedOption(fid);
			if (optTab) {
				e.preventDefault();
				selectValue(fid, optTab.getAttribute('data-pvs-value'));
				closeAll(null);
				advance(fid);
			}
		} else if (e.key === 'Escape') {
			closeAll(null);
		}
	});

	// Close on outside click
	document.addEventListener('click', function handler(e) {
		if (!_container.contains(e.target)) {
			closeAll(null);
		}
	});

	// Hover effects on options — also sync the keyboard highlight index so
	// pressing Enter right after hovering commits the row under the pointer.
	wrap.addEventListener('mouseover', function(e) {
		var opt = e.target.closest('.pvs-option');
		if (!opt) return;
		var list = opt.closest('.pvs-options');
		if (list) {
			var all = list.querySelectorAll('.pvs-option');
			for (var i = 0; i < all.length; i++) {
				all[i].classList.remove('hovered');
				if (all[i] === opt) _highlight = i;
			}
		}
		opt.classList.add('hovered');
	});
}

function refreshOptions(fid, query) {
	var allOpts = getOptions(fid);
	if (query) {
		var q = query.toLowerCase();
		// Separator-insensitive too, so "cx3" matches "CX-3 (DK)" (customers type
		// the model without the hyphen/space) — mirrors the SPA SearchableSelect.
		var strip = function (s) { return String(s).toLowerCase().replace(/[\s\-_.]/g, ''); };
		var qStripped = strip(query);
		allOpts = allOpts.filter(function (o) {
			var s = String(o).toLowerCase();
			return s.indexOf(q) !== -1 || strip(o).indexOf(qStripped) !== -1;
		});
	}
	hideTip();
	var list = _container.querySelector('[data-pvs-list="'+fid+'"]');
	if (list) renderOptions(list, allOpts, _sel[fid]);
	// Reset the highlight to the first row whenever the visible set changes
	// so ArrowDown/Enter always start from a predictable target.
	_highlight = 0;
	applyHighlight(fid);
	// The panel is open (laid out) here, so truncation can be measured.
	if (list) decorateTruncated(list);
}

// ─── Keyboard highlight ───────────────────────────────────────────────────
// Paints the `.hovered` style on the option at index `_highlight` (clamped to
// the visible range) and scrolls it into view. Shares the `.hovered` class
// with mouse hover so keyboard and pointer highlighting stay visually unified.
function optionEls(fid) {
	var list = _container.querySelector('[data-pvs-list="'+fid+'"]');
	return list ? list.querySelectorAll('.pvs-option') : [];
}
function applyHighlight(fid) {
	var opts = optionEls(fid);
	if (!opts.length) { _highlight = 0; return; }
	if (_highlight < 0) _highlight = 0;
	if (_highlight > opts.length - 1) _highlight = opts.length - 1;
	for (var i = 0; i < opts.length; i++) {
		if (i === _highlight) {
			opts[i].classList.add('hovered');
			opts[i].scrollIntoView({ block: 'nearest' });
		} else {
			opts[i].classList.remove('hovered');
		}
	}
}
function moveHighlight(fid, delta) {
	var opts = optionEls(fid);
	if (!opts.length) return;
	_highlight = Math.max(0, Math.min(opts.length - 1, _highlight + delta));
	applyHighlight(fid);
	// Keyboard: reveal the full name of a truncated highlighted row.
	showTipForRow(highlightedOption(fid), 'kbd');
}
/** The option element the keyboard is currently on, falling back to the
 *  first visible option so Enter/Tab still commit something sensible. */
function highlightedOption(fid) {
	var opts = optionEls(fid);
	if (!opts.length) return null;
	return opts[_highlight] || opts[0];
}

function getOptions(fid) {
	if (fid === 'make') return _data.makes;
	// Model list shares the backwards-filter with render() — when a year
	// is already picked, only show models active in that year.
	if (fid === 'model') return modelsForMake();
	if (fid === 'engine') return (_sel.make && _sel.model && _data.engines[_sel.make] && _data.engines[_sel.make][_sel.model]) ? _data.engines[_sel.make][_sel.model] : [];
	if (fid === 'year') {
		// Use model-specific years when make+model are selected
		if (_sel.make && _sel.model && _data.yearsByModel[_sel.make] && _data.yearsByModel[_sel.make][_sel.model] && _data.yearsByModel[_sel.make][_sel.model].length > 0) {
			return _data.yearsByModel[_sel.make][_sel.model];
		}
		return _data.years;
	}
	return [];
}

// Heavy per-make fields (engines / yearsByModel) load on demand — the initial
// payload is just the index. Fills _data.engines[make] + _data.yearsByModel[make]
// so all existing accessors keep working. Idempotent + deduped per make; mirrors
// useVehicleMakeDetail in the React app.
var _makeDetailLoading = {};
function ensureMakeDetail(make) {
	if (!make) return;
	// Already loaded (map has this make) or a fetch is in flight → nothing to do.
	if (_data.engines[make] || _makeDetailLoading[make]) return;
	_makeDetailLoading[make] = true;
	apiFetch('/api/filters/vehicle/make/' + encodeURIComponent(make))
		.then(function(res) { if (!res.ok) throw new Error('make detail ' + res.status); return res.json(); })
		.then(function(json) {
			_data.engines[make] = json.engines || {};
			_data.yearsByModel[make] = json.yearsByModel || {};
			// Re-render only if this make is still the selected one.
			if (_sel.make === make) render();
		})
		.catch(function(err) {
			console.error('PartsVehicleSelector: make detail fetch error', err);
			// Leave unloaded so variant/year fall back gracefully and a later
			// selection can retry.
			delete _makeDetailLoading[make];
		});
}

function selectValue(fid, val) {
	if (fid === 'make') {
		_sel = { make: val, model: '', engine: '', year: '' };
		ensureMakeDetail(val);
	} else if (fid === 'model') {
		_sel.model = val;
		_sel.engine = '';
		// Keep year — the model is now constrained TO the year, so we
		// don't want to wipe the user's prior backwards-filter choice.
		// (Was clearing year too — that defeated the make→year→model flow.)
	} else if (fid === 'engine') {
		_sel.engine = val;
	} else if (fid === 'year') {
		// If picking this year invalidates the current model, drop the
		// stale model + engine so the user isn't left with a "ghost"
		// selection that no longer appears in the filtered model list.
		if (_sel.make && _sel.model) {
			var ys = _data.yearsByModel && _data.yearsByModel[_sel.make] && _data.yearsByModel[_sel.make][_sel.model];
			if (!ys || ys.indexOf(Number(val)) === -1) {
				_sel.model = '';
				_sel.engine = '';
			}
		}
		_sel.year = val;
	}
	render();
}

function clearField(fid) {
	if (fid === 'make') {
		_sel = { make: '', model: '', engine: '', year: '' };
	} else if (fid === 'model') {
		_sel.model = '';
		_sel.engine = '';
		_sel.year = '';
	} else if (fid === 'engine') {
		_sel.engine = '';
	} else if (fid === 'year') {
		_sel.year = '';
	}
	render();
}

function doFindParts() {
	var params = new URLSearchParams();
	// Only emit the params the user actually picked — empty model/year
	// would otherwise become `?model=&year=` and confuse the parts-finder
	// (it parses empty strings as "filter set" rather than "absent").
	if (_sel.make) params.set('make', _sel.make);
	if (_sel.model) params.set('model', _sel.model);
	if (_sel.year) params.set('year', _sel.year);
	if (_sel.engine) params.set('engine', _sel.engine);
	params.set('page', '1');
	params.set('pageSize', '25');
	params.set('sort', 'relevance');
	try {
		// Navigate to /parts-finder on the parent window's own origin (the storefront),
		// not the parts-finder app origin that hosts this script.
		var parentOrigin = window.top.location.origin;
		var dest = parentOrigin + '/parts-finder?' + params.toString();
		window.top.location.href = dest;
	} catch (e) {
		// Cross-origin restriction — fall back to the app base URL
		var dest = getAppBaseUrl().replace(/\/$/, '') + '/parts-finder?' + params.toString();
		window.location.href = dest;
	}
}

// ─── Loading / error states ───────────────────────────────────────────────
function showLoading() {
	_container.innerHTML = '<div class="pvs-wrap"><div class="pvs-card" style="display:flex;flex-direction:column;gap:12px;">'
		+ '<div style="height:14px;border-radius:6px;background:#dee2e6;width:60%;animation:pvs-pulse 1.2s infinite ease-in-out;"></div>'
		+ '<div style="height:6px;border-radius:6px;background:#dee2e6;animation:pvs-pulse 1.2s infinite ease-in-out;"></div>'
		+ '<div style="display:flex;gap:8px;">'
		+ '<div style="flex:1;height:44px;border-radius:8px;background:#dee2e6;animation:pvs-pulse 1.2s infinite ease-in-out;"></div>'.repeat(4)
		+ '</div></div></div>';
}

function showError(msg) {
	_container.innerHTML = '<div class="pvs-wrap"><p style="padding:16px;color:#721c24;background:#f8d7da;border-radius:8px;font-size:14px;">'
		+ esc(msg || 'Could not load vehicle filters.') + '</p></div>';
}

// ─── Bootstrap ───────────────────────────────────────────────────────────
export function init() {
	// Per-instance config now lives on the target container, not the <script>
	// tag (there's only one shared bundle script now). No-op cleanly when the
	// widget isn't on this page.
	_container = getTopDocument().querySelector(CONTAINER_SELECTOR);
	if (!_container) return;

	showLoading();
	if (!getAppBaseUrl()) { showError('Parts Finder: could not determine app URL.'); return; }
	// apiFetch attaches the shared base URL + Bearer key; throw on non-OK so the
	// catch below renders the error state (matches the legacy fetch behaviour).
	// Load the light index (makes/models/years, ~67KB). The two per-make-heavy
	// fields (engines/yearsByModel) are fetched on demand per make via
	// ensureMakeDetail — see the note there.
	apiFetch('/api/filters/vehicle/index')
		.then(function(res) {
			if (!res.ok) throw new Error('Vehicle filters: ' + res.status);
			return res.json();
		})
		.then(function(json) {
			_data = {
				makes: json.makes || [],
				models: json.models || {},
				years: (json.years || []).slice().sort(function(a, b){ return b - a; }),
				yearsByModel: {}, // filled lazily, keyed by make
				engines: {},      // filled lazily, keyed by make
			};
			render();
			// If a make is already selected (e.g. restored selection), warm its detail.
			if (_sel.make) ensureMakeDetail(_sel.make);
		})
		.catch(function(err) {
			console.error('PartsVehicleSelector: fetch error', err);
			showError('Could not load vehicle filters. Please try again later.');
		});
}

export var id = 'vehicle-selector';
